import React, { useMemo, useState, Fragment, Component } from 'react';
import { Settings, Cpu, Shield, ShieldAlert, DollarSign, Zap, RefreshCw, Key, Clock, AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronRight, Save, Activity, TrendingUp, Server, Wifi, WifiOff, BarChart3, Star, Ban, ArrowDown, ArrowUp, GripVertical, Plus, Trash2, Eye, EyeOff, Copy, RotateCcw, Timer, Gauge, Layers, FileText, Target, History, User, PenLine, X, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
interface AIEngineControlCenterProps {
  success: (msg: string) => void;
}
// --- Collapsible Section (stable reference, defined outside component) ---
function Section({
  id,
  icon: Icon,
  title,
  badge,
  children,
  isOpen,
  onToggle
}: {
  id: string;
  icon: any;
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: (id: string) => void;
}) {
  return <Card padding="none" className="shadow-sm border-slate-200 overflow-hidden" data-id="element-8812">
      <button onClick={() => onToggle(id)} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors" data-id="element-8813">
        <div className="flex items-center gap-3" data-id="element-8814">
          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center" data-id="element-8815">
            <Icon className="w-5 h-5 text-slate-600" data-id="element-8816" />
          </div>
          <h3 className="text-sm font-bold text-slate-900" data-id="element-8817">{title}</h3>
          {badge}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-8818" />
      </button>
      {isOpen && <div className="px-5 pb-5 border-t border-slate-100 pt-5" data-id="element-8819">
          {children}
        </div>}
    </Card>;
}
// --- TYPES ---
interface ModelConfig {
  id: string;
  name: string;
  provider: string;
  enabled: boolean;
  priority: number;
  costPer1k: number;
  maxTokens: number;
  avgLatency: number;
  status: 'online' | 'degraded' | 'offline';
  errorRate: number;
  usageToday: number;
}
interface ApiKeyConfig {
  id: string;
  name: string;
  provider: string;
  key: string;
  enabled: boolean;
  rateLimit: number;
  usageToday: number;
  usageLimit: number;
  status: 'active' | 'expired' | 'rate_limited';
}
interface ChangeLogEntry {
  id: string;
  user: string;
  field: string;
  oldValue: string;
  newValue: string;
  timestamp: string;
}
// --- MOCK DATA ---
const MODELS: ModelConfig[] = [{
  id: 'gpt5',
  name: 'GPT-5',
  provider: 'OpenAI',
  enabled: true,
  priority: 1,
  costPer1k: 0.03,
  maxTokens: 32768,
  avgLatency: 18,
  status: 'online',
  errorRate: 1.2,
  usageToday: 4280
}, {
  id: 'claude35',
  name: 'Claude 3.5 Sonnet',
  provider: 'Anthropic',
  enabled: true,
  priority: 2,
  costPer1k: 0.025,
  maxTokens: 200000,
  avgLatency: 22,
  status: 'online',
  errorRate: 2.1,
  usageToday: 2840
}, {
  id: 'gpt4o',
  name: 'GPT-4o',
  provider: 'OpenAI',
  enabled: true,
  priority: 3,
  costPer1k: 0.015,
  maxTokens: 128000,
  avgLatency: 15,
  status: 'online',
  errorRate: 1.8,
  usageToday: 1200
}, {
  id: 'llama3',
  name: 'Llama 3 70B',
  provider: 'Meta (Self-hosted)',
  enabled: false,
  priority: 4,
  costPer1k: 0.005,
  maxTokens: 8192,
  avgLatency: 35,
  status: 'degraded',
  errorRate: 5.2,
  usageToday: 320
}, {
  id: 'gemini',
  name: 'Gemini 2.0 Pro',
  provider: 'Google',
  enabled: false,
  priority: 5,
  costPer1k: 0.02,
  maxTokens: 32768,
  avgLatency: 20,
  status: 'offline',
  errorRate: 0,
  usageToday: 0
}];
const TEMPLATE_MODEL_MAP = [{
  template: 'Натальная карта',
  model: 'GPT-5',
  fallback: 'Claude 3.5'
}, {
  template: 'Соляр',
  model: 'GPT-5',
  fallback: 'Claude 3.5'
}, {
  template: 'Совместимость',
  model: 'Claude 3.5 Sonnet',
  fallback: 'GPT-5'
}, {
  template: 'Прогноз',
  model: 'Claude 3.5 Sonnet',
  fallback: 'GPT-4o'
}, {
  template: 'Комбо',
  model: 'GPT-5',
  fallback: 'Claude 3.5'
}];
const API_KEYS: ApiKeyConfig[] = [{
  id: 'key1',
  name: 'OpenAI Production',
  provider: 'OpenAI',
  key: 'sk-proj-****...Xk9m',
  enabled: true,
  rateLimit: 500,
  usageToday: 4280,
  usageLimit: 10000,
  status: 'active'
}, {
  id: 'key2',
  name: 'OpenAI Backup',
  provider: 'OpenAI',
  key: 'sk-proj-****...Lp3n',
  enabled: true,
  rateLimit: 200,
  usageToday: 0,
  usageLimit: 5000,
  status: 'active'
}, {
  id: 'key3',
  name: 'Anthropic Main',
  provider: 'Anthropic',
  key: 'sk-ant-****...Qw7r',
  enabled: true,
  rateLimit: 300,
  usageToday: 2840,
  usageLimit: 8000,
  status: 'active'
}, {
  id: 'key4',
  name: 'Meta Self-hosted',
  provider: 'Meta',
  key: 'meta-****...Yz2k',
  enabled: false,
  rateLimit: 100,
  usageToday: 320,
  usageLimit: 2000,
  status: 'rate_limited'
}];
const CHANGE_LOG: ChangeLogEntry[] = [{
  id: '1',
  user: 'Админ Алексей',
  field: 'Основная модель',
  oldValue: 'GPT-4o',
  newValue: 'GPT-5',
  timestamp: '2026-03-17T10:30:00Z'
}, {
  id: '2',
  user: 'Админ Мария',
  field: 'Дневной бюджет',
  oldValue: '$50',
  newValue: '$75',
  timestamp: '2026-03-16T14:22:00Z'
}, {
  id: '3',
  user: 'Система',
  field: 'Fallback активирован',
  oldValue: 'GPT-5',
  newValue: 'Claude 3.5 (auto)',
  timestamp: '2026-03-16T09:15:00Z'
}, {
  id: '4',
  user: 'Админ Алексей',
  field: 'Max retry',
  oldValue: '2',
  newValue: '3',
  timestamp: '2026-03-15T16:45:00Z'
}, {
  id: '5',
  user: 'Система',
  field: 'Llama 3 отключён',
  oldValue: 'enabled',
  newValue: 'disabled (error rate >5%)',
  timestamp: '2026-03-15T08:10:00Z'
}, {
  id: '6',
  user: 'Админ Мария',
  field: 'Температура (Натальная)',
  oldValue: '0.7',
  newValue: '0.8',
  timestamp: '2026-03-14T11:30:00Z'
}, {
  id: '7',
  user: 'Админ Алексей',
  field: 'RPM лимит',
  oldValue: '300',
  newValue: '500',
  timestamp: '2026-03-13T09:00:00Z'
}, {
  id: '8',
  user: 'Система',
  field: 'API Key ротация',
  oldValue: 'key1',
  newValue: 'key2 (rate limit)',
  timestamp: '2026-03-12T22:15:00Z'
}];
const STATUS_ICON: Record<string, {
  icon: any;
  color: string;
  label: string;
}> = {
  online: {
    icon: Wifi,
    color: 'text-emerald-500',
    label: 'Online'
  },
  degraded: {
    icon: AlertTriangle,
    color: 'text-amber-500',
    label: 'Degraded'
  },
  offline: {
    icon: WifiOff,
    color: 'text-rose-500',
    label: 'Offline'
  }
};
export function AIEngineControlCenter({
  success
}: AIEngineControlCenterProps) {
  const [models, setModels] = useState(MODELS);
  const [apiKeys, setApiKeys] = useState(API_KEYS);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['models']));
  const [showKeyId, setShowKeyId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  // Settings state
  const [settings, setSettings] = useState({
    // Fallback
    fallbackEnabled: true,
    fallbackOnTimeout: true,
    fallbackOnError: true,
    fallbackOnLowQuality: true,
    maxFallbackAttempts: 3,
    // Retry
    retryEnabled: true,
    maxRetry: 3,
    retryDelay: 5,
    retryOnApiError: true,
    retryOnTimeout: true,
    retryOnLowResponse: true,
    // Limits
    globalRpm: 500,
    perUserRpm: 10,
    perUserDaily: 50,
    rateLimitEnabled: true,
    spamProtection: true,
    // Tokens
    maxTokensPerRequest: 8192,
    maxTokensPerReport: 16384,
    overflowWarning: true,
    // Cost
    dailyBudget: 75,
    monthlyBudget: 2000,
    alertThreshold: 80,
    costAlertEnabled: true,
    // Quality
    minRating: 3.0,
    autoRetryLowQuality: true,
    aiSelfCheck: true,
    autoDisableOnErrors: true,
    errorThreshold: 5,
    // General
    timeout: 120,
    temperature: 0.7,
    // Monitoring
    liveMonitoring: true,
    autoSwitch: true
  });
  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };
  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleModel = (id: string) => {
    setModels(prev => prev.map(m => m.id === id ? {
      ...m,
      enabled: !m.enabled
    } : m));
    setHasChanges(true);
  };
  const moveModel = (id: string, dir: 'up' | 'down') => {
    setModels(prev => {
      const idx = prev.findIndex(m => m.id === id);
      if (dir === 'up' && idx === 0 || dir === 'down' && idx === prev.length - 1) return prev;
      const next = [...prev];
      const swap = dir === 'up' ? idx - 1 : idx + 1;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next.map((m, i) => ({
        ...m,
        priority: i + 1
      }));
    });
    setHasChanges(true);
  };
  const toggleApiKey = (id: string) => {
    setApiKeys(prev => prev.map(k => k.id === id ? {
      ...k,
      enabled: !k.enabled
    } : k));
    setHasChanges(true);
  };
  const saveAll = () => {
    setHasChanges(false);
    success('Все настройки сохранены');
  };
  const Toggle = ({
    checked,
    onChange,
    label,
    desc
  }: {
    checked: boolean;
    onChange: (v: boolean) => void;
    label: string;
    desc?: string;
  }) => <div className="flex items-start justify-between py-3" data-id="element-8820">
      <div data-id="element-8821">
        <div className="text-sm font-medium text-slate-700" data-id="element-8822">{label}</div>
        {desc && <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-8823">{desc}</div>}
      </div>
      <button onClick={() => onChange(!checked)} className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? 'bg-indigo-600' : 'bg-slate-300'}`} data-id="element-8824">
        <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${checked ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} data-id="element-8825" />
      </button>
    </div>;
  const NumberField = ({
    label,
    value,
    onChange,
    desc,
    suffix
  }: {
    label: string;
    value: number;
    onChange: (v: number) => void;
    desc?: string;
    suffix?: string;
  }) => <div data-id="element-8826">
      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8827">
        {label}
      </label>
      <div className="flex items-center gap-2" data-id="element-8828">
        <input type="number" value={value} onChange={e => onChange(Number(e.target.value))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none" data-id="element-8829" />
        {suffix && <span className="text-xs text-slate-500 shrink-0" data-id="element-8830">{suffix}</span>}
      </div>
      {desc && <p className="text-[10px] text-slate-500 mt-1" data-id="element-8831">{desc}</p>}
    </div>;
  // --- Live Status Bar ---
  const liveStats = useMemo(() => {
    const active = models.filter(m => m.enabled && m.status === 'online');
    const totalUsage = models.reduce((s, m) => s + m.usageToday, 0);
    const totalCost = models.reduce((s, m) => s + m.usageToday * m.costPer1k / 1000, 0);
    return {
      activeModels: active.length,
      totalModels: models.length,
      totalUsage,
      totalCost: totalCost.toFixed(2),
      budgetPct: (totalCost / settings.dailyBudget * 100).toFixed(0)
    };
  }, [models, settings.dailyBudget]);
  return <div className="space-y-5" data-id="element-8832">
      {/* Live Status Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-id="element-8833">
        {[{
        label: 'Модели онлайн',
        value: `${liveStats.activeModels}/${liveStats.totalModels}`,
        icon: Cpu,
        color: 'bg-emerald-100 text-emerald-600'
      }, {
        label: 'Генераций сегодня',
        value: liveStats.totalUsage.toLocaleString(),
        icon: Activity,
        color: 'bg-indigo-100 text-indigo-600'
      }, {
        label: 'Расход сегодня',
        value: `$${liveStats.totalCost}`,
        icon: DollarSign,
        color: 'bg-amber-100 text-amber-600'
      }, {
        label: 'Бюджет использован',
        value: `${liveStats.budgetPct}%`,
        icon: Gauge,
        color: Number(liveStats.budgetPct) > 80 ? 'bg-rose-100 text-rose-600' : 'bg-blue-100 text-blue-600'
      }, {
        label: 'Auto-switch',
        value: settings.autoSwitch ? 'Вкл' : 'Выкл',
        icon: RefreshCw,
        color: settings.autoSwitch ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
      }].map(s => <Card key={s.label} padding="sm" className="shadow-sm border-slate-200" data-id="element-8834">
            <div className="flex items-center gap-3 p-1" data-id="element-8835">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${s.color}`} data-id="element-8836">
                <s.icon className="w-4 h-4" data-id="element-8837" />
              </div>
              <div data-id="element-8838">
                <div className="text-lg font-bold text-slate-900" data-id="element-8839">
                  {s.value}
                </div>
                <div className="text-[10px] text-slate-500" data-id="element-8840">{s.label}</div>
              </div>
            </div>
          </Card>)}
      </div>

      {/* Save Bar */}
      {hasChanges && <div className="sticky top-0 z-50 flex items-center justify-between p-3 bg-indigo-600 text-white rounded-xl shadow-lg" data-id="element-8841">
          <div className="flex items-center gap-2" data-id="element-8842">
            <AlertCircle className="w-4 h-4" data-id="element-8843" />
            <span className="text-sm font-medium" data-id="element-8844">
              Есть несохранённые изменения
            </span>
          </div>
          <div className="flex gap-2" data-id="element-8845">
            <Button variant="ghost" size="sm" className="text-white hover:bg-indigo-500" onClick={() => {
          setHasChanges(false);
          success('Изменения отменены');
        }} data-id="element-8846">
              Отменить
            </Button>
            <button onClick={saveAll} className="px-4 py-1.5 bg-white text-indigo-600 rounded-lg text-sm font-bold hover:bg-indigo-50 flex items-center gap-2" data-id="element-8847">
              <Save className="w-4 h-4" data-id="element-8848" /> Сохранить всё
            </button>
          </div>
        </div>}

      {/* 1. MODELS */}
      <Section id="models" icon={Cpu} title="Модели AI" isOpen={expandedSections.has('models')} onToggle={toggleSection} badge={<Badge variant="success" size="sm" data-id="element-8850">
            {liveStats.activeModels} активных
          </Badge>} data-id="element-8849">
        <div className="space-y-3 mb-6" data-id="element-8851">
          {models.map((m, idx) => {
          const st = STATUS_ICON[m.status];
          return <div key={m.id} className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${m.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`} data-id="element-8852">
                <div className="flex flex-col gap-1 shrink-0" data-id="element-8853">
                  <button onClick={() => moveModel(m.id, 'up')} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-8854">
                    <ArrowUp className="w-3.5 h-3.5" data-id="element-8855" />
                  </button>
                  <button onClick={() => moveModel(m.id, 'down')} disabled={idx === models.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-8856">
                    <ArrowDown className="w-3.5 h-3.5" data-id="element-8857" />
                  </button>
                </div>
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500" data-id="element-8858">
                  #{m.priority}
                </div>
                <div className="flex-1 min-w-0" data-id="element-8859">
                  <div className="flex items-center gap-2 mb-0.5" data-id="element-8860">
                    <span className="text-sm font-bold text-slate-900" data-id="element-8861">
                      {m.name}
                    </span>
                    <span className="text-[10px] text-slate-400" data-id="element-8862">
                      {m.provider}
                    </span>
                    <st.icon className={`w-3.5 h-3.5 ${st.color}`} data-id="element-8863" />
                    <span className={`text-[10px] ${st.color}`} data-id="element-8864">
                      {st.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-[10px] text-slate-500" data-id="element-8865">
                    <span data-id="element-8866">${m.costPer1k}/1K tok</span>
                    <span data-id="element-8867">Max: {m.maxTokens.toLocaleString()} tok</span>
                    <span data-id="element-8868">Latency: {m.avgLatency}s</span>
                    <span data-id="element-8869">
                      Errors:{' '}
                      <strong className={m.errorRate > 3 ? 'text-rose-600' : 'text-slate-600'} data-id="element-8870">
                        {m.errorRate}%
                      </strong>
                    </span>
                    <span data-id="element-8871">Сегодня: {m.usageToday.toLocaleString()}</span>
                  </div>
                </div>
                <button onClick={() => toggleModel(m.id)} className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors shrink-0 ${m.enabled ? 'bg-indigo-600' : 'bg-slate-300'}`} data-id="element-8872">
                  <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${m.enabled ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} data-id="element-8873" />
                </button>
              </div>;
        })}
        </div>

        {/* Model per template */}
        <div className="border-t border-slate-100 pt-5" data-id="element-8874">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8875">
            Модель по типу отчёта
          </h4>
          <div className="overflow-x-auto" data-id="element-8876">
            <table className="w-full text-left" data-id="element-8877">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200" data-id="element-8878">
                <tr data-id="element-8879">
                  <th className="px-3 py-2" data-id="element-8880">Шаблон</th>
                  <th className="px-3 py-2" data-id="element-8881">Основная модель</th>
                  <th className="px-3 py-2" data-id="element-8882">Fallback</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-8883">
                {TEMPLATE_MODEL_MAP.map(t => <tr key={t.template} data-id="element-8884">
                    <td className="px-3 py-2.5 text-sm text-slate-900 font-medium" data-id="element-8885">
                      {t.template}
                    </td>
                    <td className="px-3 py-2.5" data-id="element-8886">
                      <select className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" defaultValue={t.model} onChange={() => setHasChanges(true)} data-id="element-8887">
                        {models.filter(m => m.enabled).map(m => <option key={m.id} value={m.name} data-id="element-8888">
                              {m.name}
                            </option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-8889">
                      <select className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" defaultValue={t.fallback} onChange={() => setHasChanges(true)} data-id="element-8890">
                        {models.filter(m => m.enabled).map(m => <option key={m.id} value={m.name} data-id="element-8891">
                              {m.name}
                            </option>)}
                      </select>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* 2. FALLBACK LOGIC */}
      <Section id="fallback" icon={RefreshCw} title="Fallback логика" isOpen={expandedSections.has('fallback')} onToggle={toggleSection} badge={settings.fallbackEnabled ? <Badge variant="success" size="sm" data-id="element-8893">
              Активна
            </Badge> : <Badge variant="neutral" size="sm" data-id="element-8894">
              Выкл
            </Badge>} data-id="element-8892">
        <Toggle checked={settings.fallbackEnabled} onChange={v => updateSetting('fallbackEnabled', v)} label="Включить fallback" desc="Автоматическое переключение на резервную модель" data-id="element-8895" />
        <div className="border-t border-slate-100 pt-4 mt-2" data-id="element-8896">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8897">
            Fallback Chain (приоритет)
          </h4>
          <div className="flex items-center gap-2 flex-wrap mb-4" data-id="element-8898">
            {models.filter(m => m.enabled).map((m, i, arr) => <Fragment key={m.id} data-id="element-8899">
                  <div className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-900 flex items-center gap-2" data-id="element-8900">
                    <span className="w-5 h-5 rounded bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold" data-id="element-8901">
                      {i + 1}
                    </span>
                    {m.name}
                  </div>
                  {i < arr.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300" data-id="element-8902" />}
                </Fragment>)}
          </div>
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8903">
            Условия переключения
          </h4>
          <div className="space-y-1" data-id="element-8904">
            <Toggle checked={settings.fallbackOnTimeout} onChange={v => updateSetting('fallbackOnTimeout', v)} label="При timeout" desc="Переключить если модель не ответила за указанное время" data-id="element-8905" />
            <Toggle checked={settings.fallbackOnError} onChange={v => updateSetting('fallbackOnError', v)} label="При API ошибке" desc="Переключить при 5xx, rate limit, connection error" data-id="element-8906" />
            <Toggle checked={settings.fallbackOnLowQuality} onChange={v => updateSetting('fallbackOnLowQuality', v)} label="При низком качестве" desc="Переключить если AI self-check определил плохой ответ" data-id="element-8907" />
          </div>
          <div className="mt-4" data-id="element-8908">
            <NumberField label="Макс. попыток fallback" value={settings.maxFallbackAttempts} onChange={v => updateSetting('maxFallbackAttempts', v)} desc="Сколько моделей попробовать перед ошибкой" data-id="element-8909" />
          </div>
        </div>
      </Section>

      {/* 3. RETRY LOGIC */}
      <Section id="retry" icon={RotateCcw} title="Retry логика" isOpen={expandedSections.has('retry')} onToggle={toggleSection} badge={settings.retryEnabled ? <Badge variant="success" size="sm" data-id="element-8911">
              Активна
            </Badge> : <Badge variant="neutral" size="sm" data-id="element-8912">
              Выкл
            </Badge>} data-id="element-8910">
        <Toggle checked={settings.retryEnabled} onChange={v => updateSetting('retryEnabled', v)} label="Включить retry" desc="Повторная попытка генерации при ошибке" data-id="element-8913" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-8914">
          <NumberField label="Макс. retry" value={settings.maxRetry} onChange={v => updateSetting('maxRetry', v)} desc="1–5 попыток" data-id="element-8915" />
          <NumberField label="Задержка между retry" value={settings.retryDelay} onChange={v => updateSetting('retryDelay', v)} desc="Секунды между попытками" suffix="сек" data-id="element-8916" />
        </div>
        <div className="border-t border-slate-100 pt-4 mt-4 space-y-1" data-id="element-8917">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-8918">
            Условия retry
          </h4>
          <Toggle checked={settings.retryOnApiError} onChange={v => updateSetting('retryOnApiError', v)} label="При API ошибке" desc="500, 502, 503, connection refused" data-id="element-8919" />
          <Toggle checked={settings.retryOnTimeout} onChange={v => updateSetting('retryOnTimeout', v)} label="При timeout" desc="Модель не ответила за указанное время" data-id="element-8920" />
          <Toggle checked={settings.retryOnLowResponse} onChange={v => updateSetting('retryOnLowResponse', v)} label="При коротком ответе" desc="Ответ меньше минимальной длины" data-id="element-8921" />
        </div>
      </Section>

      {/* 4. LIMITS & PROTECTION */}
      <Section id="limits" icon={Shield} title="Лимиты и защита" isOpen={expandedSections.has('limits')} onToggle={toggleSection} data-id="element-8922">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8923">
          <NumberField label="Глобальный RPM" value={settings.globalRpm} onChange={v => updateSetting('globalRpm', v)} desc="Макс. запросов в минуту (все пользователи)" suffix="req/min" data-id="element-8924" />
          <NumberField label="RPM на пользователя" value={settings.perUserRpm} onChange={v => updateSetting('perUserRpm', v)} desc="Макс. запросов в минуту от одного пользователя" suffix="req/min" data-id="element-8925" />
          <NumberField label="Дневной лимит на пользователя" value={settings.perUserDaily} onChange={v => updateSetting('perUserDaily', v)} desc="Макс. генераций в день от одного пользователя" suffix="gen/day" data-id="element-8926" />
          <NumberField label="Timeout генерации" value={settings.timeout} onChange={v => updateSetting('timeout', v)} desc="Макс. время ожидания ответа от модели" suffix="сек" data-id="element-8927" />
        </div>
        <div className="border-t border-slate-100 pt-4 mt-4 space-y-1" data-id="element-8928">
          <Toggle checked={settings.rateLimitEnabled} onChange={v => updateSetting('rateLimitEnabled', v)} label="Rate limiting" desc="Ограничение частоты запросов по IP и пользователю" data-id="element-8929" />
          <Toggle checked={settings.spamProtection} onChange={v => updateSetting('spamProtection', v)} label="Защита от спама" desc="Блокировка подозрительных паттернов запросов" data-id="element-8930" />
        </div>
      </Section>

      {/* 5. TOKENS */}
      <Section id="tokens" icon={Zap} title="Токены" isOpen={expandedSections.has('tokens')} onToggle={toggleSection} data-id="element-8931">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8932">
          <NumberField label="Max tokens per request" value={settings.maxTokensPerRequest} onChange={v => updateSetting('maxTokensPerRequest', v)} desc="Максимум токенов на один запрос к модели" data-id="element-8933" />
          <NumberField label="Max tokens per report" value={settings.maxTokensPerReport} onChange={v => updateSetting('maxTokensPerReport', v)} desc="Максимум токенов на весь отчёт (включая retry)" data-id="element-8934" />
        </div>
        <div className="mt-4" data-id="element-8935">
          <div className="flex items-center gap-3" data-id="element-8936">
            <label className="block text-sm font-medium text-slate-700" data-id="element-8937">
              Температура
            </label>
            <span className="text-sm font-mono bg-slate-100 px-2 py-1 rounded" data-id="element-8938">
              {settings.temperature}
            </span>
          </div>
          <input type="range" min="0" max="1" step="0.1" value={settings.temperature} onChange={e => updateSetting('temperature', parseFloat(e.target.value))} className="w-full mt-2 accent-indigo-600" data-id="element-8939" />
          <div className="flex justify-between text-[10px] text-slate-400 mt-1" data-id="element-8940">
            <span data-id="element-8941">0.0 — Точнее</span>
            <span data-id="element-8942">1.0 — Креативнее</span>
          </div>
        </div>
        <div className="mt-4" data-id="element-8943">
          <Toggle checked={settings.overflowWarning} onChange={v => updateSetting('overflowWarning', v)} label="Предупреждение при overflow" desc="Алерт если генерация превышает лимит токенов" data-id="element-8944" />
        </div>
      </Section>

      {/* 6. COST & BUDGET */}
      <Section id="cost" icon={DollarSign} title="Стоимость и бюджет" isOpen={expandedSections.has('cost')} onToggle={toggleSection} badge={Number(liveStats.budgetPct) > 80 ? <Badge variant="error" size="sm" data-id="element-8946">
              {liveStats.budgetPct}% бюджета
            </Badge> : <Badge variant="success" size="sm" data-id="element-8947">
              {liveStats.budgetPct}% бюджета
            </Badge>} data-id="element-8945">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8948">
          <NumberField label="Дневной бюджет" value={settings.dailyBudget} onChange={v => updateSetting('dailyBudget', v)} desc="Максимальный расход в день" suffix="$" data-id="element-8949" />
          <NumberField label="Месячный бюджет" value={settings.monthlyBudget} onChange={v => updateSetting('monthlyBudget', v)} desc="Максимальный расход в месяц" suffix="$" data-id="element-8950" />
        </div>
        <div className="mt-4" data-id="element-8951">
          <NumberField label="Порог алерта" value={settings.alertThreshold} onChange={v => updateSetting('alertThreshold', v)} desc="Отправить уведомление при достижении % бюджета" suffix="%" data-id="element-8952" />
        </div>
        <div className="mt-4" data-id="element-8953">
          <Toggle checked={settings.costAlertEnabled} onChange={v => updateSetting('costAlertEnabled', v)} label="Алерты о бюджете" desc="Уведомления в Telegram/Email при превышении порога" data-id="element-8954" />
        </div>
        <div className="border-t border-slate-100 pt-4 mt-4" data-id="element-8955">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8956">
            Стоимость по моделям
          </h4>
          <div className="space-y-2" data-id="element-8957">
            {models.filter(m => m.enabled).map(m => {
            const cost = m.usageToday * m.costPer1k / 1000;
            return <div key={m.id} className="flex items-center justify-between py-2" data-id="element-8958">
                    <div className="flex items-center gap-2" data-id="element-8959">
                      <Cpu className="w-3.5 h-3.5 text-slate-400" data-id="element-8960" />
                      <span className="text-sm text-slate-700" data-id="element-8961">{m.name}</span>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-8962">
                      <span className="text-xs text-slate-500" data-id="element-8963">
                        {m.usageToday.toLocaleString()} gen
                      </span>
                      <span className="text-sm font-bold text-slate-900" data-id="element-8964">
                        ${cost.toFixed(2)}
                      </span>
                    </div>
                  </div>;
          })}
          </div>
        </div>
      </Section>

      {/* 7. QUALITY */}
      <Section id="quality" icon={Star} title="Контроль качества" isOpen={expandedSections.has('quality')} onToggle={toggleSection} data-id="element-8965">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8966">
          <div data-id="element-8967">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8968">
              Минимальный рейтинг для retry
            </label>
            <div className="flex items-center gap-3" data-id="element-8969">
              <input type="range" min="1" max="5" step="0.5" value={settings.minRating} onChange={e => updateSetting('minRating', parseFloat(e.target.value))} className="flex-1 accent-indigo-600" data-id="element-8970" />
              <span className="text-sm font-mono bg-slate-100 px-2 py-1 rounded flex items-center gap-1" data-id="element-8971">
                <Star className="w-3 h-3 text-amber-500 fill-amber-500" data-id="element-8972" />
                {settings.minRating}
              </span>
            </div>
            <p className="text-[10px] text-slate-500 mt-1" data-id="element-8973">
              Отчёты с рейтингом ниже → автоматический retry
            </p>
          </div>
          <NumberField label="Порог ошибок для отключения модели" value={settings.errorThreshold} onChange={v => updateSetting('errorThreshold', v)} desc="Если error rate модели превышает %" suffix="%" data-id="element-8974" />
        </div>
        <div className="border-t border-slate-100 pt-4 mt-4 space-y-1" data-id="element-8975">
          <Toggle checked={settings.autoRetryLowQuality} onChange={v => updateSetting('autoRetryLowQuality', v)} label="Авто-retry при низком качестве" desc="Повторная генерация если рейтинг ниже порога" data-id="element-8976" />
          <Toggle checked={settings.aiSelfCheck} onChange={v => updateSetting('aiSelfCheck', v)} label="AI Self-Check" desc="Модель проверяет свой ответ перед отправкой" data-id="element-8977" />
          <Toggle checked={settings.autoDisableOnErrors} onChange={v => updateSetting('autoDisableOnErrors', v)} label="Авто-отключение при ошибках" desc="Отключить модель если error rate превышает порог" data-id="element-8978" />
          <Toggle checked={settings.autoSwitch} onChange={v => updateSetting('autoSwitch', v)} label="Auto-switch при падении" desc="Автоматическое переключение на fallback при недоступности" data-id="element-8979" />
        </div>
      </Section>

      {/* 8. API KEYS */}
      <Section id="apikeys" icon={Key} title="API ключи" isOpen={expandedSections.has('apikeys')} onToggle={toggleSection} badge={<Badge variant="outline" size="sm" data-id="element-8981">
            {apiKeys.filter(k => k.enabled).length} активных
          </Badge>} data-id="element-8980">
        <div className="space-y-3" data-id="element-8982">
          {apiKeys.map(k => <div key={k.id} className={`flex items-center gap-4 p-4 rounded-xl border ${k.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`} data-id="element-8983">
              <div className="flex-1 min-w-0" data-id="element-8984">
                <div className="flex items-center gap-2 mb-1" data-id="element-8985">
                  <span className="text-sm font-bold text-slate-900" data-id="element-8986">
                    {k.name}
                  </span>
                  <Badge variant="outline" size="sm" className="text-[10px]" data-id="element-8987">
                    {k.provider}
                  </Badge>
                  <Badge variant={k.status === 'active' ? 'success' : k.status === 'rate_limited' ? 'warning' : 'error'} size="sm" className="text-[10px]" data-id="element-8988">
                    {k.status === 'active' ? 'Active' : k.status === 'rate_limited' ? 'Rate Limited' : 'Expired'}
                  </Badge>
                </div>
                <div className="flex items-center gap-3" data-id="element-8989">
                  <code className="text-xs font-mono text-slate-500 bg-slate-50 px-2 py-0.5 rounded" data-id="element-8990">
                    {showKeyId === k.id ? k.key.replace('****', 'abcd-efgh-ijkl') : k.key}
                  </code>
                  <button onClick={() => setShowKeyId(showKeyId === k.id ? null : k.id)} className="text-slate-400 hover:text-slate-600" data-id="element-8991">
                    {showKeyId === k.id ? <EyeOff className="w-3.5 h-3.5" data-id="element-8992" /> : <Eye className="w-3.5 h-3.5" data-id="element-8993" />}
                  </button>
                  <button onClick={() => success('Ключ скопирован')} className="text-slate-400 hover:text-slate-600" data-id="element-8994">
                    <Copy className="w-3.5 h-3.5" data-id="element-8995" />
                  </button>
                </div>
                <div className="flex items-center gap-4 text-[10px] text-slate-500 mt-1" data-id="element-8996">
                  <span data-id="element-8997">Rate limit: {k.rateLimit} RPM</span>
                  <span data-id="element-8998">
                    Сегодня: {k.usageToday.toLocaleString()} /{' '}
                    {k.usageLimit.toLocaleString()}
                  </span>
                  <div className="flex-1 max-w-[100px] h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-8999">
                    <div className={`h-full rounded-full ${k.usageToday / k.usageLimit > 0.8 ? 'bg-rose-400' : 'bg-indigo-400'}`} style={{
                  width: `${Math.min(k.usageToday / k.usageLimit * 100, 100)}%`
                }} data-id="element-9000" />
                  </div>
                </div>
              </div>
              <button onClick={() => toggleApiKey(k.id)} className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors shrink-0 ${k.enabled ? 'bg-indigo-600' : 'bg-slate-300'}`} data-id="element-9001">
                <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${k.enabled ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} data-id="element-9002" />
              </button>
            </div>)}
        </div>
        <Button variant="outline" size="sm" className="mt-4" icon={<Plus className="w-4 h-4" data-id="element-9004" />} onClick={() => success('Добавление ключа')} data-id="element-9003">
          Добавить API ключ
        </Button>
      </Section>

      {/* 9. MONITORING */}
      <Section id="monitoring" icon={Activity} title="Мониторинг и логи" isOpen={expandedSections.has('monitoring')} onToggle={toggleSection} data-id="element-9005">
        <Toggle checked={settings.liveMonitoring} onChange={v => updateSetting('liveMonitoring', v)} label="Live мониторинг" desc="Отслеживание статуса моделей в реальном времени" data-id="element-9006" />
        <div className="border-t border-slate-100 pt-4 mt-4" data-id="element-9007">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-9008">
            Статус моделей (live)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-9009">
            {models.filter(m => m.enabled).map(m => {
            const st = STATUS_ICON[m.status];
            return <div key={m.id} className={`p-3 rounded-lg border ${m.status === 'online' ? 'border-emerald-200 bg-emerald-50/30' : m.status === 'degraded' ? 'border-amber-200 bg-amber-50/30' : 'border-rose-200 bg-rose-50/30'}`} data-id="element-9010">
                    <div className="flex items-center gap-2 mb-2" data-id="element-9011">
                      <st.icon className={`w-4 h-4 ${st.color}`} data-id="element-9012" />
                      <span className="text-sm font-bold text-slate-900" data-id="element-9013">
                        {m.name}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[10px]" data-id="element-9014">
                      <div data-id="element-9015">
                        <span className="text-slate-500" data-id="element-9016">Latency:</span>{' '}
                        <strong data-id="element-9017">{m.avgLatency}s</strong>
                      </div>
                      <div data-id="element-9018">
                        <span className="text-slate-500" data-id="element-9019">Errors:</span>{' '}
                        <strong className={m.errorRate > 3 ? 'text-rose-600' : ''} data-id="element-9020">
                          {m.errorRate}%
                        </strong>
                      </div>
                      <div data-id="element-9021">
                        <span className="text-slate-500" data-id="element-9022">Usage:</span>{' '}
                        <strong data-id="element-9023">{m.usageToday}</strong>
                      </div>
                      <div data-id="element-9024">
                        <span className="text-slate-500" data-id="element-9025">Status:</span>{' '}
                        <strong className={st.color} data-id="element-9026">{st.label}</strong>
                      </div>
                    </div>
                  </div>;
          })}
          </div>
        </div>
      </Section>

      {/* 10. CHANGE HISTORY */}
      <Section id="history" icon={History} title="История изменений" isOpen={expandedSections.has('history')} onToggle={toggleSection} badge={<Badge variant="outline" size="sm" data-id="element-9028">
            {CHANGE_LOG.length} записей
          </Badge>} data-id="element-9027">
        <div className="space-y-2" data-id="element-9029">
          {CHANGE_LOG.map(entry => <div key={entry.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 bg-white" data-id="element-9030">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${entry.user === 'Система' ? 'bg-amber-100' : 'bg-indigo-100'}`} data-id="element-9031">
                {entry.user === 'Система' ? <Cpu className="w-3.5 h-3.5 text-amber-600" data-id="element-9032" /> : <User className="w-3.5 h-3.5 text-indigo-600" data-id="element-9033" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-9034">
                <div className="flex items-center gap-2 mb-0.5" data-id="element-9035">
                  <span className="text-xs font-bold text-slate-900" data-id="element-9036">
                    {entry.user}
                  </span>
                  <span className="text-[10px] text-slate-400" data-id="element-9037">
                    {new Date(entry.timestamp).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </span>
                </div>
                <p className="text-xs text-slate-600" data-id="element-9038">
                  <span className="font-medium" data-id="element-9039">{entry.field}:</span>{' '}
                  <span className="text-rose-500 line-through" data-id="element-9040">
                    {entry.oldValue}
                  </span>
                  {' → '}
                  <span className="text-emerald-600 font-medium" data-id="element-9041">
                    {entry.newValue}
                  </span>
                </p>
              </div>
            </div>)}
        </div>
      </Section>

      {/* Bottom Save */}
      <div className="flex justify-end pt-2 pb-4" data-id="element-9042">
        <Button variant="primary" icon={<Save className="w-4 h-4" data-id="element-9044" />} onClick={saveAll} data-id="element-9043">
          Сохранить все настройки
        </Button>
      </div>
    </div>;
}